import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Alert, ProgressBar, Badge } from 'react-bootstrap';
import { getAvisos, getMedios } from '../services/apiService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ComparativaVentas = () => {
  // Estados para almacenar años seleccionados
  const [anioActual, setAnioActual] = useState(new Date().getFullYear());
  const [anioAnterior, setAnioAnterior] = useState(new Date().getFullYear() - 1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para datos de ventas
  const [ventasActual, setVentasActual] = useState([]);
  const [ventasAnterior, setVentasAnterior] = useState([]);
  const [comparativa, setComparativa] = useState([]);
  const [totalAvisos, setTotalAvisos] = useState(0);
  const [totalValor, setTotalValor] = useState(0);
  
  // Estado para medio (empresa proveedora)
  const [medio, setMedio] = useState('58'); // Por defecto Antonio Puga
  const [mediosDisponibles, setMediosDisponibles] = useState([]);
  
  // Estado para ejecutivo
  const [ejecutivoSeleccionado, setEjecutivoSeleccionado] = useState('');
  const [ejecutivosDisponibles, setEjecutivosDisponibles] = useState([]);
  
  // Estado para seguimiento del progreso
  const [progreso, setProgreso] = useState(0);
  const [mesesProcesados, setMesesProcesados] = useState(0);
  const [calculosCompletados, setCalculosCompletados] = useState([]);
  const [usoCache, setUsoCache] = useState(false);
  
  // Control de recálculo forzado
  const [forzarRecalculo, setForzarRecalculo] = useState(false);
  
  // Agregar nuevo estado para el cálculo en caché
  const [calculoEnCache, setCalculoEnCache] = useState(false);
  
  // Arreglo de meses
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  
  // Opciones de años disponibles (desde 2020 hasta el año actual)
  const aniosDisponibles = [];
  for (let i = 2020; i <= new Date().getFullYear(); i++) {
    aniosDisponibles.push(i);
  }
  
  // Ref para verificar si el componente está montado
  const isMounted = useRef(true);
  
  // Ref para manejar el intervalo de actualización de progreso
  const progresoInterval = useRef(null);
  
  // Cleanup del intervalo al desmontar
  useEffect(() => {
    return () => {
      if (progresoInterval.current) {
        clearInterval(progresoInterval.current);
      }
    };
  }, []);
  
  // Cargar medios disponibles
  useEffect(() => {
    const cargarMedios = async () => {
      try {
        const response = await getMedios();
        if (response && response.success) {
          setMediosDisponibles(response.data);
        }
      } catch (error) {
        console.error('Error al cargar medios:', error);
      }
    };
    
    cargarMedios();
  }, []);
  
  // Cargar ventas cuando cambian los años seleccionados
  useEffect(() => {
    if (anioActual && anioAnterior) {
      cargarVentas();
    }
  }, []);
  
  // Cargar cálculos previos
  useEffect(() => {
    cargarCalculosCompletados();
  }, []);
  
  // Función para cargar cálculos previos
  const cargarCalculosCompletados = async () => {
    try {
      const response = await fetch('/api/ventas/calculos-previos');
      const data = await response.json();
      
      if (data.success && data.calculos) {
        setCalculosCompletados(data.calculos);
      }
    } catch (error) {
      console.error('Error al cargar cálculos previos:', error);
    }
  };
  
  // Función para iniciar animación de la barra de progreso
  const iniciarAnimacionProgreso = () => {
    // Limpiar intervalo anterior si existe
    if (progresoInterval.current) {
      clearInterval(progresoInterval.current);
    }
    
    setProgreso(5); // Iniciar en 5%
    
    // Configurar intervalo para simular progreso hasta 90%
    progresoInterval.current = setInterval(() => {
      setProgreso(progreso => {
        if (progreso >= 90) {
          clearInterval(progresoInterval.current);
          return progreso;
        }
        // Incrementos más pequeños al principio, mayores cerca del final
        const incremento = progreso < 30 ? 5 : (progreso < 60 ? 3 : 1);
        return Math.min(90, progreso + incremento);
      });
    }, 500);
  };
  
  // Función para cargar cálculo específico desde caché
  const cargarCalculoDesdeCache = (anioAct, anioAnt, medioId, ejecutivo) => {
    setAnioActual(anioAct);
    setAnioAnterior(anioAnt);
    setMedio(medioId);
    setEjecutivoSeleccionado(ejecutivo);
    setForzarRecalculo(false);
    setUsoCache(true);
    setCalculoEnCache(true);
    
    // Generar la clave de caché
    const cacheKey = `${anioAct}-${anioAnt}-${medioId}${ejecutivo ? `-${ejecutivo}` : ''}`;
    
    // Hacer la solicitud para obtener los datos de la caché
    fetch(`/api/ventas/comparativa?${new URLSearchParams({
      anioActual: anioAct,
      anioAnterior: anioAnt,
      medio: medioId,
      ...(ejecutivo && { ejecutivo })
    })}`)
    .then(response => response.json())
    .then(data => {
      if (data.success && data.fromCache) {
        setComparativa(generarComparativaDesdeAPI(data.comparativa));
        setProgreso(100);
      } else {
        // Si no está en caché, hacer un cálculo normal
        cargarVentas();
      }
    })
    .catch(error => {
      console.error('Error al cargar datos desde caché:', error);
      cargarVentas();
    });
  };
  
  // Función para cargar los datos de ventas
  const cargarVentas = async () => {
    setIsLoading(true);
    setError(null);
    setProgreso(0);
    setMesesProcesados(0);
    
    iniciarAnimacionProgreso();
    
    if (forzarRecalculo) {
      try {
        await fetch('/api/ventas/calculos-previos', {
          method: 'DELETE'
        });
        console.log('Caché limpiada por recálculo forzado');
      } catch (cacheError) {
        console.error('Error al limpiar la caché:', cacheError);
      }
    }
    
    try {
      // Construir los parámetros de la API
      const params = new URLSearchParams({
        anioActual,
        anioAnterior,
        medio
      });

      // Agregar el ejecutivo solo si está seleccionado
      if (ejecutivoSeleccionado) {
        params.append('ejecutivo', ejecutivoSeleccionado);
      }
      
      if (forzarRecalculo) {
        params.append('forzarRecalculo', 'true');
      }
      
      console.log(`Consultando comparativa entre ${anioAnterior} y ${anioActual} para medio ${medio}${ejecutivoSeleccionado ? ` y ejecutivo ${ejecutivoSeleccionado}` : ''}...`);
      
      const response = await fetch(`/api/ventas/comparativa?${params}`);
      const data = await response.json();
      
      if (progresoInterval.current) {
        clearInterval(progresoInterval.current);
      }
      
      if (data.success) {
        setComparativa(generarComparativaDesdeAPI(data.comparativa));
        
        if (data.fromCache) {
          console.log('Datos obtenidos desde caché');
          setUsoCache(true);
          setCalculoEnCache(true);
        } else {
          console.log('Datos calculados nuevamente');
          setUsoCache(false);
          setCalculoEnCache(false);
        }
        
        cargarCalculosCompletados();
        setProgreso(100);
      } else {
        setError(data.message || 'Error al obtener datos de comparativa');
        setProgreso(0);
      }
    } catch (error) {
      console.error('Error al cargar ventas:', error);
      setError('Error de conexión al servidor. Por favor, intente de nuevo más tarde.');
      setProgreso(0);
    } finally {
      setIsLoading(false);
      setForzarRecalculo(false);
      isMounted.current = true;
    }
  };
  
  // Función para cargar ventas por año
  const cargarVentasPorAnio = async (anio) => {
    const ventasPorMes = Array(12).fill(0);
    
    // Obtener el mes y año actual
    const fechaActual = new Date();
    const mesActual = fechaActual.getMonth(); // 0-11
    const anioActual = fechaActual.getFullYear();
    
    // Cargar datos para cada mes del año
    for (let mes = 0; mes < 12; mes++) {
      try {
        // Si el año es el actual y el mes es mayor al mes actual, dejamos los datos en cero
        if (anio === anioActual && mes > mesActual) {
          console.log(`Mes ${meses[mes]} del ${anio} es posterior al mes actual. Se mantiene en cero.`);
          continue;
        }
        
        // Actualizamos el progreso
        const nuevoProgreso = Math.round(((mes + 1) / 24) * 100); // 24 meses en total (12 por año)
        setProgreso(prevProgreso => Math.max(prevProgreso, nuevoProgreso));
        setMesesProcesados(prevMeses => prevMeses + 1);
        
        const fechaInicio = `${anio}-${String(mes + 1).padStart(2, '0')}-01`;
        
        // Calcular último día del mes
        const ultimoDia = new Date(anio, mes + 1, 0).getDate();
        const fechaFin = `${anio}-${String(mes + 1).padStart(2, '0')}-${ultimoDia}`;
        
        const filtros = {
          fechaInicio,
          fechaFin,
          medio // Usar el medio seleccionado
        };
        
        const response = await getAvisos(filtros);
        
        if (response.success) {
          // Sumar todos los valores de los avisos
          const totalMes = response.data.reduce(
            (sum, aviso) => sum + parseFloat(aviso.valor_con_descuento || 0), 
            0
          );
          
          ventasPorMes[mes] = totalMes;
        }
      } catch (error) {
        console.error(`Error al cargar ventas para ${meses[mes]} de ${anio}:`, error);
      }
    }
    
    return ventasPorMes;
  };
  
  // Función para generar comparativa desde datos de la API
  const generarComparativaDesdeAPI = (datosAPI) => {
    return meses.map((mes, index) => {
      const item = datosAPI[index] || { montoActual: 0, montoAnterior: 0 };
      const valorActual = item.montoActual || 0;
      const valorAnterior = item.montoAnterior || 0;
      
      // Calcular diferencia en dinero
      const diferenciaDinero = valorActual - valorAnterior;
      
      // Calcular diferencia porcentual
      let diferenciaPorcentaje = 0;
      if (valorAnterior > 0) {
        diferenciaPorcentaje = (diferenciaDinero / valorAnterior) * 100;
      } else if (valorActual > 0) {
        diferenciaPorcentaje = 100; // Si antes era 0 y ahora hay ventas, aumento del 100%
      }
      
      return {
        mes: meses[index],
        valorActual,
        valorAnterior,
        diferenciaDinero,
        diferenciaPorcentaje
      };
    }).concat([{
      mes: 'TOTAL ANUAL',
      valorActual: datosAPI.reduce((sum, item) => sum + (item.montoActual || 0), 0),
      valorAnterior: datosAPI.reduce((sum, item) => sum + (item.montoAnterior || 0), 0),
      diferenciaDinero: datosAPI.reduce((sum, item) => sum + (item.montoActual || 0), 0) - datosAPI.reduce((sum, item) => sum + (item.montoAnterior || 0), 0),
      diferenciaPorcentaje: datosAPI.reduce((sum, item) => sum + (item.montoAnterior || 0), 0) > 0 ? 
        ((datosAPI.reduce((sum, item) => sum + (item.montoActual || 0), 0) - datosAPI.reduce((sum, item) => sum + (item.montoAnterior || 0), 0)) / datosAPI.reduce((sum, item) => sum + (item.montoAnterior || 0), 0)) * 100 : 0
    }]);
  };
  
  // Función para generar comparativa
  const generarComparativa = (ventasActual, ventasAnterior) => {
    const datosComparativa = meses.map((mes, index) => {
      const valorActual = ventasActual[index] || 0;
      const valorAnterior = ventasAnterior[index] || 0;
      
      // Calcular diferencia en dinero
      const diferenciaDinero = valorActual - valorAnterior;
      
      // Calcular diferencia porcentual
      let diferenciaPorcentaje = 0;
      if (valorAnterior > 0) {
        diferenciaPorcentaje = (diferenciaDinero / valorAnterior) * 100;
      } else if (valorActual > 0) {
        diferenciaPorcentaje = 100; // Si antes era 0 y ahora hay ventas, aumento del 100%
      }
      
      return {
        mes,
        valorActual,
        valorAnterior,
        diferenciaDinero,
        diferenciaPorcentaje
      };
    });
    
    // Añadir totales anuales
    const totalActual = ventasActual.reduce((sum, valor) => sum + valor, 0);
    const totalAnterior = ventasAnterior.reduce((sum, valor) => sum + valor, 0);
    const diferenciaDineroTotal = totalActual - totalAnterior;
    let diferenciaPorcentajeTotal = 0;
    
    if (totalAnterior > 0) {
      diferenciaPorcentajeTotal = (diferenciaDineroTotal / totalAnterior) * 100;
    } else if (totalActual > 0) {
      diferenciaPorcentajeTotal = 100;
    }
    
    datosComparativa.push({
      mes: 'TOTAL ANUAL',
      valorActual: totalActual,
      valorAnterior: totalAnterior,
      diferenciaDinero: diferenciaDineroTotal,
      diferenciaPorcentaje: diferenciaPorcentajeTotal
    });
    
    setComparativa(datosComparativa);
  };
  
  // Función para formatear valores monetarios
  const formatearMoneda = (valor) => {
    if (valor === undefined || valor === null) return '$0';
    return `$${Math.round(valor).toLocaleString('es-CL')}`;
  };
  
  // Función para limpiar todos los cálculos en caché
  const limpiarCache = async () => {
    try {
      const response = await fetch('/api/ventas/calculos-previos', {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Caché limpiada correctamente');
        setCalculosCompletados([]);
      }
    } catch (error) {
      console.error('Error al limpiar la caché:', error);
      alert('Error al limpiar la caché');
    }
  };
  
  // Manejar cambio de año
  const handleSubmit = (e) => {
    e.preventDefault();
    cargarVentas();
  };
  
  // Manejar recálculo forzado
  const handleRecalcular = () => {
    setForzarRecalculo(true);
    setUsoCache(false);
    cargarVentas();
  };
  
  // Efecto para manejar cambios en el ejecutivo
  useEffect(() => {
    if (ejecutivoSeleccionado !== undefined) {
      setForzarRecalculo(true);
      cargarVentas();
    }
  }, [ejecutivoSeleccionado]);

  // Efecto para manejar cambios en los años y medio
  useEffect(() => {
    if (anioActual && anioAnterior && medio) {
      cargarVentas();
    }
  }, [anioActual, anioAnterior, medio]);

  // Manejador para el cambio de ejecutivo
  const handleEjecutivoChange = (event) => {
    const nuevoEjecutivo = event.target.value;
    setEjecutivoSeleccionado(nuevoEjecutivo);
    setForzarRecalculo(true);
  };

  // Cargar ejecutivos disponibles
  useEffect(() => {
    const cargarEjecutivos = async () => {
      try {
        const response = await getAvisos({ limit: 9999 });
        if (response.success) {
          const ejecutivosUnicos = [...new Set(response.data.map(aviso => aviso.ejecutivo).filter(Boolean))];
          setEjecutivosDisponibles(ejecutivosUnicos);
        }
      } catch (error) {
        console.error('Error al cargar ejecutivos:', error);
      }
    };
    
    cargarEjecutivos();
  }, []);

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">Comparativa de Ventas por Meses</h1>
          <p className="text-muted">
            Analiza la variación de ventas entre diferentes años
          </p>
        </Col>
      </Row>
      
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Año actual</Form.Label>
                  <Form.Select 
                    value={anioActual}
                    onChange={(e) => setAnioActual(parseInt(e.target.value))}
                  >
                    {aniosDisponibles.map(anio => (
                      <option key={`actual-${anio}`} value={anio}>{anio}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Año de comparación</Form.Label>
                  <Form.Select 
                    value={anioAnterior}
                    onChange={(e) => setAnioAnterior(parseInt(e.target.value))}
                  >
                    {aniosDisponibles.map(anio => (
                      <option key={`anterior-${anio}`} value={anio}>{anio}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Medio</Form.Label>
                  <Form.Select 
                    value={medio}
                    onChange={(e) => setMedio(e.target.value)}
                  >
                    {mediosDisponibles.map(m => (
                      <option key={`medio-${m.id}`} value={m.id}>{m.nombre}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Ejecutivo</Form.Label>
                  <Form.Select
                    value={ejecutivoSeleccionado}
                    onChange={handleEjecutivoChange}
                  >
                    <option value="">Todos los ejecutivos</option>
                    {ejecutivosDisponibles.map((ejecutivo) => (
                      <option key={ejecutivo} value={ejecutivo}>
                        {ejecutivo}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Button type="submit" variant="primary" className="mb-3 me-2">
                  {calculoEnCache ? 'Cargar desde caché' : 'Calcular'}
                </Button>
                
                {calculoEnCache && (
                  <Button 
                    type="button"
                    variant="warning" 
                    className="mb-3 me-2"
                    onClick={handleRecalcular}
                    disabled={isLoading}
                  >
                    Forzar recálculo
                  </Button>
                )}
                
                <Button 
                  variant="outline-danger" 
                  className="mb-3"
                  onClick={limpiarCache}
                  disabled={isLoading || calculosCompletados.length === 0}
                  title="Eliminar todos los cálculos guardados"
                >
                  Limpiar caché
                </Button>
              </Col>
            </Row>
          </Form>
          
          {calculosCompletados.length > 0 && (
            <div className="mt-2">
              <small className="text-muted">Cálculos disponibles en caché (click para cargar):</small>
              <div className="d-flex flex-wrap mt-1">
                {calculosCompletados.map((calculo, index) => (
                  <Badge 
                    bg={calculo.anioActual == anioActual && 
                        calculo.anioAnterior == anioAnterior && 
                        calculo.medio == medio &&
                        calculo.ejecutivo === ejecutivoSeleccionado ? "success" : "info"}
                    className="me-2 mb-2 px-2 py-2" 
                    key={index}
                    role="button"
                    style={{ cursor: 'pointer' }}
                    onClick={() => cargarCalculoDesdeCache(
                      calculo.anioActual, 
                      calculo.anioAnterior, 
                      calculo.medio,
                      calculo.ejecutivo
                    )}
                    title={`Cargar cálculo ${calculo.tipo === 'ejecutivo' ? 'por ejecutivo' : 'general'} para ${calculo.anioAnterior}-${calculo.anioActual}, Medio: ${mediosDisponibles.find(m => m.id == calculo.medio)?.nombre || 'No especificado'}${calculo.ejecutivo ? `, Ejecutivo: ${calculo.ejecutivo}` : ''}`}
                  >
                    {calculo.anioAnterior} → {calculo.anioActual} 
                    {calculo.medio && ` (${mediosDisponibles.find(m => m.id == calculo.medio)?.nombre?.substring(0, 15) || calculo.medio}${mediosDisponibles.find(m => m.id == calculo.medio)?.nombre?.length > 15 ? '...' : ''})`}
                    {calculo.ejecutivo ? ` - ${calculo.ejecutivo}` : ' - General'}
                    <small className="ms-1 text-muted">({calculo.tipo})</small>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {isLoading ? (
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Cargando datos de ventas...</h5>
            <ProgressBar 
              animated 
              now={progreso} 
              label={`${progreso}%`} 
              className="mb-3"
              variant="primary"
            />
            <p className="text-muted mb-0">
              Procesando mes {mesesProcesados} de 24 meses totales...
            </p>
          </Card.Body>
        </Card>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <Row>
          <Col>
            {usoCache && (
              <Alert variant="info" className="mb-3">
                <Alert.Heading>Datos cargados desde caché</Alert.Heading>
                <p className="mb-0">
                  Los datos se han cargado rápidamente desde la caché. Esto mejora significativamente el rendimiento.
                  Si desea recalcular estos datos, utilice el botón "Forzar recálculo".
                </p>
              </Alert>
            )}
            
            <Card>
              <Card.Body>
                <Table striped bordered hover responsive className="mb-0">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th>Mes</th>
                      <th className="text-end">{anioActual}</th>
                      <th className="text-end">{anioAnterior}</th>
                      <th className="text-end">Diferencia ($)</th>
                      <th className="text-end">Variación (%)</th>
                      <th className="text-end">Variación Acumulada (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparativa.map((item, index) => {
                      // Obtener el mes actual
                      const fechaActual = new Date();
                      const mesActual = fechaActual.getMonth();
                      
                      // Calcular la variación acumulada correctamente
                      let variacionAcumulada = null;
                      
                      if (index <= mesActual && item.mes !== 'TOTAL ANUAL') {
                        // Calcular total acumulado hasta el mes actual para ambos años
                        const totalAcumuladoActual = comparativa
                          .slice(0, index + 1)
                          .reduce((acc, curr) => {
                            if (curr.mes === 'TOTAL ANUAL') return acc;
                            return acc + curr.valorActual;
                          }, 0);
                          
                        const totalAcumuladoAnterior = comparativa
                          .slice(0, index + 1)
                          .reduce((acc, curr) => {
                            if (curr.mes === 'TOTAL ANUAL') return acc;
                            return acc + curr.valorAnterior;
                          }, 0);
                        
                        // Calcular variación acumulada según la fórmula correcta
                        if (totalAcumuladoAnterior > 0) {
                          variacionAcumulada = ((totalAcumuladoActual - totalAcumuladoAnterior) / totalAcumuladoAnterior) * 100;
                        } else if (totalAcumuladoActual > 0) {
                          variacionAcumulada = 100; // Si no había ventas acumuladas el año anterior
                        }
                      }

                      return (
                        <tr key={index} className={item.mes === 'TOTAL ANUAL' ? 'table-info fw-bold' : ''}>
                          <td>{item.mes}</td>
                          <td className="text-end">{formatearMoneda(item.valorActual)}</td>
                          <td className="text-end">{formatearMoneda(item.valorAnterior)}</td>
                          <td className={`text-end ${item.diferenciaDinero >= 0 ? 'text-success' : 'text-danger'}`}>
                            {item.diferenciaDinero >= 0 ? '+' : ''}{formatearMoneda(item.diferenciaDinero)}
                          </td>
                          <td className={`text-end ${item.diferenciaPorcentaje >= 0 ? 'text-success' : 'text-danger'}`}>
                            {item.diferenciaPorcentaje >= 0 ? '+' : ''}
                            {item.diferenciaPorcentaje.toFixed(2)}%
                          </td>
                          <td className={`text-end ${variacionAcumulada !== null ? (variacionAcumulada >= 0 ? 'text-success' : 'text-danger') : ''}`}>
                            {variacionAcumulada !== null ? (
                              <>
                                {variacionAcumulada >= 0 ? '+' : ''}
                                {variacionAcumulada.toFixed(2)}%
                              </>
                            ) : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ComparativaVentas; 